import * as Sentry from '@sentry/vue';
import { ref } from 'vue';
import type { Ref } from 'vue';
import type { CommitmentResponse } from '@@/types/api';
import { ApiError } from '~/composables/api/api-error';
import { useCommitmentsApi } from '~/composables/api/use-commitments-api';
import { runPostSuccess } from '~/utils/run-post-success';

interface UseTypeSwitchActionsOptions {
  eventId: Ref<string>;
  commitment: Ref<CommitmentResponse | null>;
  enrich: () => Promise<void>;
  refreshPublicEvent: () => Promise<void>;
}

export function useTypeSwitchActions(options: UseTypeSwitchActionsOptions) {
  const { eventId, commitment, enrich, refreshPublicEvent } = options;
  const commitmentsApi = useCommitmentsApi();
  const { trackEvent } = useMatomo();

  const showTypeSwitchDialog = ref(false);
  const isSwitchingType = ref(false);

  const snackbar = ref(false);
  const snackbarText = ref('');
  const snackbarColor = ref('success');

  function showSnackbar(text: string, color = 'success') {
    snackbarText.value = text;
    snackbarColor.value = color;
    snackbar.value = true;
  }

  function openTypeSwitchDialog() {
    showTypeSwitchDialog.value = true;
  }

  async function handleTypeSwitchSubmit(targetTypeId: string) {
    if (!commitment.value) return;
    const commitmentId = commitment.value.id;
    const version = commitment.value.version ?? 1;
    isSwitchingType.value = true;
    // The outer `try`/`finally` owns `isSwitchingType` and resets it only
    // after the post-success refresh settles, so the loading flag spans the
    // whole action.
    try {
      // Only the API call lives in the inner `try`. A type switch can change
      // what the backer is charged, so a tracking or refresh failure after it
      // succeeded must never surface as "Failed to switch type." — the user
      // would retry, hit a stale `version`, and get a confusing 409. Tracking
      // and refresh run through `runPostSuccess` instead (#1980).
      try {
        await commitmentsApi.switchType(eventId.value, commitmentId, {
          registration_type_id: targetTypeId,
          version,
        });
      } catch (e: unknown) {
        Sentry.captureException(e, {
          tags: { operation: 'type_switch', event_id: eventId.value },
        });
        if (e instanceof ApiError && e.isNetworkError) {
          showSnackbar(
            'Network error — check your connection and try again.',
            'error',
          );
        } else if (
          e &&
          typeof e === 'object' &&
          'name' in e &&
          e.name === 'ApiError'
        ) {
          const apiErr = e as unknown as { status: number; message: string };
          if (apiErr.status === 409) {
            // Version conflict — re-enrich and let user retry
            await enrich();
            showSnackbar(
              'This commitment was modified. Please try again.',
              'warning',
            );
          } else if (apiErr.status === 422) {
            showSnackbar(apiErr.message || 'Unable to switch type.', 'error');
          } else {
            showSnackbar('Failed to switch type. Please try again.', 'error');
          }
        } else {
          showSnackbar('Failed to switch type. Please try again.', 'error');
        }
        return;
      }

      showTypeSwitchDialog.value = false;
      showSnackbar('Registration type switched successfully.');

      await runPostSuccess('type_switch', eventId.value, () =>
        trackEvent('Commitment', 'TypeSwitched', eventId.value),
      );
      await runPostSuccess('type_switch', eventId.value, () =>
        Promise.all([enrich(), refreshPublicEvent()]),
      );
    } finally {
      isSwitchingType.value = false;
    }
  }

  return {
    showTypeSwitchDialog,
    isSwitchingType,
    snackbar,
    snackbarText,
    snackbarColor,
    openTypeSwitchDialog,
    handleTypeSwitchSubmit,
  };
}
